/**
 * Router-compat shim — bridges react-router-dom v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import { useMemo, useCallback, useEffect, useRef, forwardRef, type ComponentProps, type ReactNode } from "react";

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search?: Record<string, string>; hash?: string } {
  const parts = (to ?? "").split("#");
  const beforeHash = parts[0] ?? "";
  const hashStr = parts[1];
  const pathParts = beforeHash.split("?");
  const pathname = pathParts[0] ?? "";
  const searchStr = pathParts[1];
  const result: { pathname: string; search?: Record<string, string>; hash?: string } = {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
  };
  if (searchStr) result.search = Object.fromEntries(new URLSearchParams(searchStr));
  if (hashStr) result.hash = hashStr;
  return result;
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    const navOpts: Record<string, unknown> = { to: pathname };
    if (search !== undefined) navOpts['search'] = search;
    if (hash !== undefined) navOpts['hash'] = hash;
    if (options?.state !== undefined) navOpts['state'] = options.state;
    if (options?.replace !== undefined) navOpts['replace'] = options.replace;
    tsNav(navOpts as never);
  }, [tsNav, router]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tsParams({ strict: false } as never) as T;
}


// ---------- useSearchParams (react-router-dom compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => { searchObj[k] = v; });
      const navOpts: Record<string, unknown> = { to: live.pathname, search: searchObj };
      if (opts?.replace !== undefined) navOpts['replace'] = opts.replace;
      nav(navOpts as never);
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

export type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  const extraProps: Record<string, unknown> = {};
  if (search !== undefined) extraProps['search'] = search;
  if (hash !== undefined) extraProps['hash'] = hash;
  if (replace !== undefined) extraProps['replace'] = replace;
  if (state !== undefined) extraProps['state'] = state;
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      {...(extraProps as Record<string, unknown>)}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  // Navigate once after commit. Rendering the router's <Navigate> during a
  // pending client transition re-triggered navigation in a loop and froze the page.
  const navigate = useNavigate();
  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    const opts: NavigateOptions = {};
    if (replace !== undefined) opts.replace = replace;
    if (state !== undefined) opts.state = state;
    navigate(to, opts);
  }, [navigate, to, replace, state]);
  return null;
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;
